<template>
  <b-card>
    <b-form>
      <fieldset :disabled="loading">
        <b-form-group id="userId-group" :label="$t('userId')" label-for="userId">
          <b-form-input size="sm" id="userId" type="text" v-model="filter.userId" :disabled=isUserAsActiveProfile(this.activeProfile) v-on:keyup="onKeyup"/>
        </b-form-group>

        <b-form-group id="organizationNumber-group" :label="$t('organizationNumber')" label-for="organizationNumber">
          <b-form-input size="sm" id="organizationNumber" type="text" v-model="filter.organizationNumber" :disabled=isOrganizationAsActiveProfile(this.activeProfile) v-on:keyup="onKeyup"/>
        </b-form-group>

        <b-form-group id="orderId-group" :label="$t('orderId')" label-for="orderId">
          <b-form-input size="sm" id="orderId" type="text" v-model="filter.orderId" v-on:keyup="onKeyup" @input="clearSourceOrderLineId"/>
        </b-form-group>

        <b-form-group id="sourceOrderLine-group" :label="$t('sourceOrderLine')" label-for="sourceOrderLine">
          <b-form-input size="sm" id="sourceOrderLine" type="text" :disabled=isOrderIdProvided v-model="filter.sourceOrderLineId" v-on:keyup="onKeyup"/>
        </b-form-group>

        <b-form-group id="product-group" :label="$t('product')" label-for="product">
          <b-form-input size="sm" id="product" type="text" v-model="filter.product" v-on:keyup="onKeyup"/>
        </b-form-group>

        <b-form-group id="status-group" :label="$t('status')" label-for="status">
          <b-form-select size="sm" id="status" v-model="filter.status" :options="licenseStatuses" v-on:change="updateLicenses">
            <template slot="first">
              <option :value="null">{{ $t('all') }}</option>
            </template>
          </b-form-select>
        </b-form-group>

        <b-form-group :label="$t('licenseModel')" label-for="licenseModel">
          <b-form-tags
            id="licenseModel-select"
            v-model="filter.licenseModel"
            size="mb"
            class="mb-2"
            add-on-change
            no-outer-focus
          >
            <template v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }">
              <ul v-if="tags.length > 0" class="list-inline d-inline-block mb-2">
                <li v-for="tag in tags" :key="tag" class="list-inline-item">
                  <b-form-tag
                    @remove="removeTag(tag)"
                    :disabled="disabled"
                    variant="info"
                  > {{ translateAlternatives('licenseModels', tag) }}</b-form-tag>
                </li>
              </ul>
              <b-form-select
                v-bind="inputAttrs"
                v-on="inputHandlers"
                :options="licenseModels"
              >
                <template slot="first">
                  <option disabled value="">{{ $t('addLicenseModel') }}</option>
                </template>
              </b-form-select>
            </template>
          </b-form-tags>
        </b-form-group>

        <b-form-group id="valid-group" :label="$t('filter')">
          <b-form-checkbox size="sm" v-model="filter.isValid" name="checkbox" switch inline>{{ $t('onlyValid') }}</b-form-checkbox>
          <b-form-checkbox size="sm" v-model="filter.includeBundledProducts" name="checkbox" switch inline>{{ $t('includeBundledProducts') }}</b-form-checkbox>
        </b-form-group>

        <b-row>
          <b-col>
            <b-form-select size="sm" id="perPage" v-model="paging.perPage" :options="paging.perPageAlternatives"/>
          </b-col>
          <b-col>
            <b-button size="sm" class="float-right" v-on:click="resetFilter">{{ $t('reset') }}</b-button>
          </b-col>
        </b-row>
        <b-form-group>
          <pagination v-bind:pagination="paging"/>
        </b-form-group>
        <b-row>
          <b-col>
            <b-button type="button" block variant="primary" @click="showCreateLicenseModal">{{ $t('createLicense') }}</b-button>
            <create-license ref="createLicenseModal"/>
            <b-button type="button" block variant="primary" @click="showBulkCreateLicenseModal" :disabled=isActiveProfileSet>{{ $t('bulkLicenseCreation') }}</b-button>
            <create-bulk-license ref="createBulkLicenseModal"/>
          </b-col>
        </b-row>
      </fieldset>
    </b-form>
  </b-card>
</template>

<script>
  import BaseComponent from '../../components/BaseComponent'
  import { mapGetters, mapState } from 'vuex'
  import CreateLicense from './CreateLicense'
  import CreateBulkLicense from './CreateBulkLicense'

  export default {
    name: 'LicenseFilter',
    extends: BaseComponent,
    components: {
      CreateLicense,
      CreateBulkLicense
    },

    computed: {
      ...mapGetters('licenses', ['filter', 'paging', 'loading']),
      ...mapGetters('sessionStorage', ['isActiveProfileSet', 'activeProfile', 'hasActiveUser']),
      ...mapState('licenses', { filter: state => state.filter }),
      isOrderIdProvided () {
        return !this.filter.orderId
      }
    },
    methods: {
      onKeyup (event) {
        if (event.which === 13) {
          this.updateLicenses()
        }
      },
      resetFilter () {
        this.$store.dispatch('licenses/resetFilter')
      },
      updateLicenses () {
        this.$store.dispatch('licenses/getLicenses')
      },
      showCreateLicenseModal () {
        this.$store.dispatch('licenses/resetCreateLicense')
        this.$store.dispatch('licenses/getSelectedActiveProfile')
        const defaultLicenseModel = this.hasActiveUser ? 'PRIVATE' : 'SCHOOL';
        this.$store.dispatch('licenses/updateLicenseModel', defaultLicenseModel)
        this.$root.$emit('bv::show::modal', 'createLicenseModal')
      },
      showBulkCreateLicenseModal () {
        this.$store.dispatch('licenses/resetBulkParameters')
        this.$root.$emit('bv::show::modal', 'createBulkLicenseModal')
      },
      clearSourceOrderLineId () {
        if (!this.filter.orderId){
          this.$store.dispatch('licenses/resetSourceOrderLineId')
        }
      }
    },
    watch: {
      'paging.perPage': function () {
        this.updateLicenses()
      },
      'paging.currentPage': function () {
        this.updateLicenses()
      },
      'filter.isValid': function () {
        this.updateLicenses()
      },
      'isActiveProfileSet' : function () {
        this.updateLicenses()
      },
      'filter.licenseModel': function () {
        this.updateLicenses()
      },
      'filter.includeBundledProducts': function () {
        this.updateLicenses()
      }
    },
    i18n: {
      messages: {
        en: {
          'addLicenseModel': 'Add license model',
          'sourceOrderLine': 'Order line',
          'product': 'Product (ISBN, name)'
        },
        nb:{
          'addLicenseModel': 'Legg til lisensmodell',
          'sourceOrderLine': 'Ordrelinje',
          'product': 'Produkt (ISBN, navn)'
        }
      }
    }
  }
</script>
